import React, { useState, useEffect } from "react";
import { format } from "date-fns";
import {
  CheckCircleIcon,
  ChevronDoubleRightIcon,
  EyeIcon,
  XCircleIcon,
  Bars3Icon,
  DocumentChartBarIcon,
} from "@heroicons/react/20/solid";
import { VictoryVoronoiContainer, VictoryTooltip, VictoryArea, VictoryChart, VictoryAxis, VictoryTheme } from "victory";
import ManagerNav from "./managernav";
import ReactFlow, {
  Controls,
  Background,
  applyNodeChanges,
  applyEdgeChanges,
} from "reactflow";
import "reactflow/dist/style.css";
import { useLocation, useNavigate } from "react-router-dom";

export default function ProcessViewer() {
  const location = useLocation();
  const navigate = useNavigate();
  const [openProcessForm, setOpenProcessForm] = useState(false);
  const [openSteps, setOpenSteps] = useState(false);
  const [steps, setSteps] = useState([]);
  const [directory, setDirectory] = useState({});
  const [processId, setProcessId] = useState(null);
  const [nameProcess, setNameProcess] = useState("");
  const [expandedRows, setExpandedRows] = useState({});
  const [showDirectory, setShowDirectory] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportStatus, setReportStatus] = useState(null); // 'success' | 'error' | null
  const [reports, setReports] = useState([]);
  // expandedRows stores a boolean keyed by subStep id to toggle the merged table

  
  useEffect(() => {
    getProcess();
  }, []);

  useEffect(() => {
    if (location.state?.autoOpenProcessId && location.state?.autoOpenProcessName) {
      handleGetSteps(location.state.autoOpenProcessId, location.state.autoOpenProcessName);
    }
  }, [location.state]);

  const getProcess = async () => {
    try {
      const response = await fetch("https://api.insightos.co.uk/api/process", {
        method: "GET",
        headers: { }, credentials: 'include',
      });
      const data = await response.json();

      if (data.success) {
        const dir = data.data.reduce((acc, proc) => {
          const letter = proc.Name[0].toUpperCase();
          if (!acc[letter]) acc[letter] = [];
          acc[letter].push(proc);
          acc[letter].sort((a, b) => a.Name.localeCompare(b.Name));
          return acc;
        }, {});
        setDirectory(dir);
      } else {
        console.error("Failed to fetch processes:", data.message);
      }
    } catch (error) {
      console.error("Error fetching processes:", error);
    }
  };

  const handleGetSteps = async (id, name) => {
    setOpenSteps(true);
    setNameProcess(name);
    setProcessId(id);

    try {
      const response = await fetch(`https://api.insightos.co.uk/api/getstepsissues/${id}`, {
        method: "GET",
        headers: { }, credentials: 'include',
      });
      const data = await response.json();

      if (response.ok && data.success) {
        setSteps(data.data);
        setReports(Array.isArray(data.report) ? data.report : []);
      } else {
        console.error("Failed to fetch steps:", data.message);
        alert(data.message || "Error fetching tasks.");
      }
    } catch (error) {
      console.error("Error fetching steps:", error);
    }
  };

  const toggleExpandRow = (substepId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [substepId]: !prev[substepId],
    }));
  };

  // Find the maximum number of issues in a task step for the VictoryChart domain

  const toggleExpand = (helpId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [helpId]: !prev[helpId],
    }));

    // console.log(expandedRows);

  };
  const nodes = (steps || []).flatMap((step, index) => {
    const scheduledCount = step.calendar?.length || 0;
    const completedCount = step.calendar?.filter((cal) => cal.Completed).length || 0;
    const isBottleneck = scheduledCount > 0 && completedCount === 0;
    const backgroundColor = isBottleneck ? "#FF3131" : "#00FF88";
  
    // 🟢 Main Step Node
    const mainNode = {
      id: step.id?.toString() || `node-${index}`,
      data: { label: step.Task || "Unnamed Task" },
      position: { x: index * 300, y: 100 },
      style: {
        background: backgroundColor,
        color: "#fff",
        borderRadius: "10px",
        boxShadow: `0px 0px 10px ${backgroundColor}`,
        padding: "10px",
      },
    };
  
    // 🟡 Substep Nodes (With Circular Shape & Name)
    const subStepNodes = (step.subSteps || []).map((subStep, subIndex) => {
      const hasIssue = (subStep.issues || []).length > 0;
      const subStepColor = hasIssue ? "#FFD700" : "#00FF88";
  
      return {
        id: `sub-${step.id}-${subIndex}`,
        data: { label: subStep.Name || "Substep" }, // ✅ Displays Name inside circle
        position: { x: index * 300, y: 200 + subIndex * 80 },
        style: {
          background: subStepColor,
          color: "#fff",
          borderRadius: "50%", // Makes it a circle
          width: 80,
          height: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          fontSize: "12px",
          padding: "5px",
          boxShadow: `0px 0px 10px ${subStepColor}`,
          wordWrap: "break-word",
        },
      };
    });
  
    return [mainNode, ...subStepNodes];
  });
  
  // 🔗 Create edges to link substeps, tasks, and next tasks
  const edges = (steps || []).flatMap((step, index) => {
    const mainStepId = step.id?.toString() || `node-${index}`;
    const subSteps = step.subSteps || [];
  
    // 🔹 Edges between substeps
    const subStepEdges = subSteps.map((subStep, subIndex) => ({
      id: `e-${mainStepId}-sub-${subIndex}`,
      source: subIndex === 0 ? mainStepId : `sub-${step.id}-${subIndex - 1}`, // Link previous substep
      target: `sub-${step.id}-${subIndex}`,
      animated: true,
      style: { stroke: "#FFD700", strokeWidth: 2 },
    }));
  
    // 🔴 Link **last substep** to next task
    const lastSubStep = subSteps.length > 0 ? `sub-${step.id}-${subSteps.length - 1}` : null;
    const nextStep = steps[index + 1]?.id?.toString();
    const lastSubStepEdge = lastSubStep && nextStep
      ? [{
          id: `e-${lastSubStep}-next-${nextStep}`,
          source: lastSubStep,
          target: nextStep,
          animated: true,
          style: { stroke: "#00BFFF", strokeWidth: 2 }, // Blue for next step transition
        }]
      : [];
  
    return [...subStepEdges, ...lastSubStepEdge];
  });
  
 
  



  const createBottleneckReport = async () => {
    if (!processId) return;
    setReportLoading(true);
    setReportStatus(null);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/createaibottleneckreport', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ processId }),
      });
      const data = await response.json();

      if (response.status === 202 && data.queued && data.jobId) {
        navigate(`/job-progress/${data.jobId}`, {
          state: {
            title: 'Creating bottleneck report',
            successPath: '/bottleneck-analysis',
            backPath: '/bottleneck-analysis',
            successState: {
              autoOpenProcessId: processId,
              autoOpenProcessName: nameProcess,
            },
          },
        });
        return;
      }

      if (response.ok) {
        setReportStatus('success');
        await handleGetSteps(processId, nameProcess);
      } else {
        setReportStatus('error');
      }
    } catch {
      setReportStatus('error');
    } finally {
      setReportLoading(false);
      setTimeout(() => setReportStatus(null), 4000);
    }
  };

  // Prepare data for VictoryChart: aggregate total issues per task step.
  const issueData = steps.map((step) => ({
    task: step.Task,
    issues: step.subSteps.reduce((total, subStep) => total + subStep.issues.length, 0),
  }));

  return (
    <>
      <ManagerNav />
      <div data-component="training-process-viewer" className="flex flex-col lg:flex-row min-h-screen bg-gray-50">

        {/* Directory Sidebar */}
        {showDirectory && (
          <div className="w-full lg:w-72 flex-shrink-0 bg-gradient-to-b from-indigo-900 via-sky-700 to-emerald-600 z-20 fixed lg:static top-0 left-0 h-full lg:h-auto overflow-y-auto shadow-xl">
            <div className="px-5 py-5 border-b border-white/20">
              <h2 className="text-white font-bold text-base tracking-wide">Process Library</h2>
              <p className="text-white/50 text-xs mt-0.5">Select a process to analyse</p>
            </div>
            <nav aria-label="Directory" className="p-3 space-y-1">
              {Object.keys(directory).sort().map((letter) => (
                <div key={letter}>
                  <div className="px-2 pt-3 pb-1 text-xs font-bold text-white/40 uppercase tracking-widest">{letter}</div>
                  {directory[letter].map((proc) => (
                    <div
                      key={proc.id}
                      data-component="training-directory-card"
                      data-feature={`open-${proc.id}`}
                      className="flex justify-between items-center px-3 py-3 mb-1 bg-white/10 hover:bg-white/20 border border-white/10 rounded-xl text-white cursor-pointer transition-all duration-150 group"
                      onClick={() => {
                        handleGetSteps(proc.id, proc.Name);
                        if (window.innerWidth < 1024) setShowDirectory(false);
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold truncate">{proc.Name}</p>
                        <p className="mt-0.5 truncate text-xs text-white/50">{proc.Description}</p>
                        <p className="mt-0.5 text-xs text-emerald-300">{proc.StaffName}</p>
                      </div>
                      <ChevronDoubleRightIcon className="w-4 h-4 ml-2 text-white/40 group-hover:text-white flex-shrink-0 transition-colors" />
                    </div>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 p-4 sm:p-6 overflow-y-auto">

          {/* Empty state */}
          {!openSteps && (
            <div className="flex items-center justify-center h-72">
              <div className="text-center">
                <div className="mx-auto w-16 h-16 rounded-2xl bg-gradient-to-br from-indigo-500 to-emerald-500 flex items-center justify-center mb-4 shadow-lg">
                  <EyeIcon className="w-8 h-8 text-white" />
                </div>
                <p className="text-gray-500 font-medium text-sm">Select a process from the library to view its steps and insights.</p>
              </div>
            </div>
          )}

          {openSteps && (
            <>
              {/* Page header */}
              <div className="flex flex-wrap justify-between items-center mb-6 gap-3">
                <div className="flex items-center gap-3">
                  {!showDirectory && (
                    <button
                      data-component="training-directory-toggle"
                      data-feature="open-menu"
                      className="p-2 rounded-xl bg-gradient-to-br from-indigo-600 to-sky-500 text-white shadow hover:opacity-90 transition"
                      onClick={() => setShowDirectory(true)}
                    >
                      <Bars3Icon className="w-5 h-5" />
                    </button>
                  )}
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{nameProcess}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">{steps.length} task step{steps.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  {reportStatus === 'success' && (
                    <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-xl">Report created successfully</span>
                  )}
                  {reportStatus === 'error' && (
                    <span className="text-xs font-semibold text-red-600 bg-red-50 border border-red-200 px-3 py-1.5 rounded-xl">Failed to create report</span>
                  )}
                  <button
                    className="flex items-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500 text-white font-semibold text-sm shadow-lg hover:opacity-90 transition disabled:opacity-60"
                    onClick={createBottleneckReport}
                    disabled={reportLoading}
                  >
                    <DocumentChartBarIcon className="w-4 h-4" />
                    {reportLoading ? 'Creating…' : 'Create Report'}
                  </button>
                </div>
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
                {/* Issue Count Chart */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-800">Issue Count by Task Step</h3>
                  <p className="text-xs text-gray-400 mt-0.5 mb-3">Total issues logged per process task</p>
                  <div className="overflow-x-auto">
                    <VictoryChart
                      height={180}
                      width={400}
                      domainPadding={{ x: 15, y: 5 }}
                      containerComponent={<VictoryVoronoiContainer />}
                      theme={VictoryTheme.material}
                    >
                      <VictoryAxis
                        tickValues={issueData.map((d) => d.task)}
                        tickFormat={issueData.map((d) => d.task)}
                        style={{
                          tickLabels: { fill: "#9ca3af", fontSize: 5, angle: -30, textAnchor: "end" },
                          axis: { stroke: "#e5e7eb" },
                          grid: { stroke: "#f3f4f6", strokeDasharray: "4" },
                        }}
                      />
                      <VictoryAxis
                        dependentAxis
                        tickFormat={(t) => t}
                        style={{
                          tickLabels: { fill: "#9ca3af", fontSize: 8 },
                          axis: { stroke: "#e5e7eb" },
                          grid: { stroke: "#f3f4f6", strokeDasharray: "4" },
                        }}
                      />
                      <VictoryArea
                        data={issueData}
                        x="task"
                        y="issues"
                        style={{
                          data: {
                            fill: "rgba(99,102,241,0.12)",
                            stroke: "#6366f1",
                            strokeWidth: 2,
                          },
                        }}
                        labels={({ datum }) => `Issues: ${datum.issues}`}
                        labelComponent={<VictoryTooltip style={{ fill: "#1e1b4b", fontSize: 7 }} />}
                      />
                    </VictoryChart>
                  </div>
                </div>

                {/* Flow chart */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5">
                  <h3 className="text-sm font-bold text-gray-800">Process Flow Visualisation</h3>
                  <p className="text-xs text-gray-400 mt-0.5 mb-3">Task and substep dependency map</p>
                  <div style={{ width: "100%", height: "210px", borderRadius: "12px", overflow: "hidden", background: "#0f172a" }}>
                    <ReactFlow nodes={nodes} edges={edges} fitView style={{ width: "100%", height: "100%" }}>
                      <Controls />
                      <Background color="#334155" gap={16} />
                    </ReactFlow>
                  </div>
                </div>
              </div>

              {/* AI Bottleneck Reports */}
              {reports.length > 0 && (
                <div className="space-y-4 mb-6">
                  {reports.map((report, idx) => (
                    <div key={report.id ?? idx} className="bg-white rounded-2xl shadow-sm border border-indigo-100 overflow-hidden">
                      <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 bg-gradient-to-r from-indigo-600 via-sky-500 to-emerald-500">
                        <div className="flex items-center gap-2">
                          <DocumentChartBarIcon className="w-4 h-4 text-white" />
                          <span className="text-sm font-bold text-white">AI Bottleneck Report</span>
                        </div>
                        <span className="text-xs font-semibold text-white/80">
                          {report.ReportDate
                            ? format(new Date(report.ReportDate), 'dd MMM yyyy, HH:mm')
                            : 'No date'}
                        </span>
                      </div>
                      <div className="px-5 py-4">
                        <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans leading-relaxed">{report.Content}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Top Issue Hotspots table */}
              {(() => {
                const topIssues = steps
                  .flatMap((s) =>
                    s.subSteps.map((ss) => ({
                      task: s.Task,
                      substep: ss.Name,
                      count: ss.issues.length,
                    }))
                  )
                  .filter((r) => r.count > 0)
                  .sort((a, b) => b.count - a.count)
                  .slice(0, 10);

                if (topIssues.length === 0) return null;

                return (
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 mb-6">
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                      <div>
                        <h3 className="text-sm font-bold text-gray-800">Top Issue Hotspots</h3>
                        <p className="text-xs text-gray-400 mt-0.5">Tasks &amp; substeps with the most reported issues</p>
                      </div>
                      <span className="px-3 py-1 text-xs font-bold rounded-full bg-red-50 text-red-600 border border-red-100">
                        {topIssues.reduce((s, r) => s + r.count, 0)} total issues
                      </span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="pb-2 pl-2 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">#</th>
                            <th className="pb-2 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Task Step</th>
                            <th className="pb-2 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Substep</th>
                            <th className="pb-2 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Issues</th>
                            <th className="pb-2 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Severity</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {topIssues.map((row, idx) => {
                            const severity =
                              row.count >= 5
                                ? { label: 'High', cls: 'bg-red-100 text-red-700' }
                                : row.count >= 3
                                ? { label: 'Medium', cls: 'bg-amber-100 text-amber-700' }
                                : { label: 'Low', cls: 'bg-emerald-100 text-emerald-700' };
                            return (
                              <tr key={idx} className="hover:bg-gray-50 transition-colors">
                                <td className="py-3 pl-2 text-xs font-bold text-gray-300">#{idx + 1}</td>
                                <td className="py-3 px-3 text-sm font-semibold text-gray-800">{row.task}</td>
                                <td className="py-3 px-3 text-sm text-gray-500">{row.substep}</td>
                                <td className="py-3 px-3">
                                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-indigo-600">
                                    <span className="w-2 h-2 rounded-full bg-indigo-400 inline-block" />
                                    {row.count}
                                  </span>
                                </td>
                                <td className="py-3 px-3">
                                  <span className={`px-2.5 py-0.5 text-xs font-bold rounded-full ${severity.cls}`}>{severity.label}</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Step detail cards */}
              {steps.map((stepItem) => {
                const scheduledCount = stepItem.calendar?.length || 0;
                const completedCount = stepItem.calendar?.filter((cal) => cal.Completed).length || 0;
                const totalIssues = stepItem.subSteps.reduce((t, ss) => t + ss.issues.length, 0);
                return (
                  <div key={stepItem.id} className="mb-4 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-indigo-50 to-sky-50">
                      <h3 className="text-sm font-bold text-indigo-900">{stepItem.Task}</h3>
                      <div className="flex items-center gap-2">
                        {totalIssues > 0 && (
                          <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-red-100 text-red-600">
                            {totalIssues} issue{totalIssues !== 1 ? 's' : ''}
                          </span>
                        )}
                        <span className="px-2.5 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700">
                          {completedCount}/{scheduledCount} Completed
                        </span>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead>
                          <tr className="border-b border-gray-100">
                            <th className="py-3 pl-5 pr-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Substep</th>
                            <th className="py-3 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Issues</th>
                            <th className="py-3 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider">Details</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {stepItem.subSteps.map((subStep) => (
                            <React.Fragment key={subStep.id}>
                              <tr className="hover:bg-gray-50 transition-colors">
                                <td className="py-3 pl-5 pr-3 text-sm font-medium text-gray-800">{subStep.Name}</td>
                                <td className="py-3 px-3">
                                  {subStep.issues.length > 0 ? (
                                    <span className="inline-flex items-center gap-1 text-sm font-bold text-red-500">
                                      <XCircleIcon className="w-4 h-4" /> {subStep.issues.length}
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-500">
                                      <CheckCircleIcon className="w-4 h-4" /> 0
                                    </span>
                                  )}
                                </td>
                                <td className="py-3 px-3">
                                  <button
                                    data-component="training-substep-toggle"
                                    data-feature={`substep-${subStep.id}`}
                                    className="text-xs font-semibold px-3 py-1 rounded-lg bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition"
                                    onClick={() => toggleExpandRow(subStep.id)}
                                  >
                                    {expandedRows[subStep.id] ? 'Hide' : 'View Details'}
                                  </button>
                                </td>
                              </tr>
                              {expandedRows[subStep.id] && (
                                <tr>
                                  <td colSpan="3" className="p-4 bg-indigo-50/40">
                                    <h4 className="text-xs font-bold text-indigo-800 uppercase tracking-wider mb-3">Issues &amp; Help Details</h4>
                                    <div className="overflow-x-auto rounded-xl border border-indigo-100">
                                      <table className="min-w-full bg-white">
                                        <thead>
                                          <tr className="border-b border-gray-100 bg-gray-50">
                                            <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/2">Issue Description</th>
                                            <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-1/2">Help Description</th>
                                            <th className="py-2.5 px-3 text-left text-xs font-semibold text-gray-400 uppercase tracking-wider w-24">Rating</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-50">
                                          {subStep.issues.map((issue) =>
                                            issue.help.length > 0 ? (
                                              issue.help.map((help) => {
                                                const isExpanded = expandedRows[help.id];
                                                return (
                                                  <tr key={help.id}>
                                                    <td className="px-3 py-3 text-sm text-gray-700 w-1/2 align-top">
                                                      <div className={`overflow-hidden ${isExpanded ? '' : 'line-clamp-5'}`}>
                                                        {issue.Description} - {issue.Tried}
                                                      </div>
                                                      {issue.Description.split('\n').length > 10 && (
                                                        <button
                                                          data-component="training-detail-toggle"
                                                          data-feature={`issue-${help.id}`}
                                                          onClick={() => toggleExpand(help.id)}
                                                          className="text-indigo-500 text-xs font-semibold mt-1 block hover:underline"
                                                        >
                                                          {isExpanded ? 'Show Less' : 'Read More'}
                                                        </button>
                                                      )}
                                                    </td>
                                                    <td className="px-3 py-3 text-sm text-gray-700 w-1/2 align-top">
                                                      <div className={`overflow-hidden ${isExpanded ? '' : 'line-clamp-5'}`}>
                                                        {help.Description}
                                                      </div>
                                                      {help.Description.split('\n').length > 10 && (
                                                        <button
                                                          data-component="training-detail-toggle"
                                                          data-feature={`help-${help.id}`}
                                                          onClick={() => toggleExpand(help.id)}
                                                          className="text-indigo-500 text-xs font-semibold mt-1 block hover:underline"
                                                        >
                                                          {isExpanded ? 'Show Less' : 'Read More'}
                                                        </button>
                                                      )}
                                                    </td>
                                                    <td className="px-3 py-3 text-sm text-amber-400 w-24 align-top">{"⭐".repeat(help.Rating)}</td>
                                                  </tr>
                                                );
                                              })
                                            ) : (
                                              <tr key={issue.id}>
                                                <td className="px-3 py-3 text-sm text-gray-700 w-1/2 align-top">{issue.Description}</td>
                                                <td className="px-3 py-3 text-sm text-gray-400 align-top" colSpan="2">No help data recorded</td>
                                              </tr>
                                            )
                                          )}
                                        </tbody>
                                      </table>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </>
  );
}
